"use client";
import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import "../../components/Common/AdminDetailModal.css";
import "./UserDetailsModal.css";

export default function UserDetailsModal({
  modal,
  toggle,
  user,
  onStatusUpdate,
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleStatusUpdate = async (newStatus) => {
    if (!user || !user.id) return;

    setIsUpdating(true);
    try {
      const response = await Api.put(`/admin/users/${user.id}/status`, {
        status: newStatus,
      });

      if (response.data.success) {
        toast.success(
          `User ${
            newStatus === "active" ? "activated" : "deactivated"
          } successfully`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );

        // Call parent callback to update the user list
        if (onStatusUpdate) {
          onStatusUpdate(user.id, newStatus, response.data.data.state);
        }

        // Close modal
        toggle();
      } else {
        toast.error(response.data.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Status update error:", error);
      toast.error(
        error.response?.data?.message || "Failed to update user status"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleApproveUser = async () => {
    if (!user || !user.id) return;

    setIsUpdating(true);
    try {
      const response = await Api.put(`/admin/users/${user.id}/approve`);

      if (response.data.success) {
        toast.success("User approved and activated successfully", {
          position: "top-right",
          autoClose: 3000,
        });

        // Call parent callback to update the user list
        if (onStatusUpdate) {
          onStatusUpdate(user.id, "active", 1);
        }

        // Close modal
        toggle();
      } else {
        toast.error(response.data.message || "Failed to approve user");
      }
    } catch (error) {
      console.error("Approve user error:", error);
      toast.error(error.response?.data?.message || "Failed to approve user");
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: "success", text: "Active" },
      inactive: { color: "secondary", text: "Inactive" },
    };
    return statusConfig[status] || { color: "secondary", text: "Unknown" };
  };

  const getStateBadge = (state) => {
    const stateConfig = {
      0: { color: "warning", text: "Not Approved" },
      1: { color: "success", text: "Approved" },
    };
    return stateConfig[state] || { color: "secondary", text: "Unknown" };
  };

  const getUserTypeBadge = (userType) => {
    const typeConfig = {
      Admin: { color: "primary", text: "Admin" },
      Influencer: { color: "info", text: "Influencer" },
      Business: { color: "success", text: "Business" },
      User: { color: "secondary", text: "User" },
    };
    return typeConfig[userType] || { color: "secondary", text: "User" };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!user) return null;

  const statusBadge = getStatusBadge(user.status);
  const stateBadge = getStateBadge(user.state);
  const typeBadge = getUserTypeBadge(user.user_type);

  return (
    <Modal
      isOpen={modal}
      toggle={toggle}
      size="lg"
      className="admin-detail-modal user-details-modal"
      centered
    >
      <ModalHeader toggle={toggle} className="border-0">
        <div className="adm-header-inner d-flex align-items-center flex-wrap gap-3">
          <div className="user-avatar">
            {(user.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="adm-modal-title">User Details</div>
            <div className="text-muted small mb-0">Complete user information</div>
          </div>
        </div>
      </ModalHeader>

        <ModalBody>
          {/* Basic Information */}
          <div className="info-card">
            <div className="section-title">
              <i className="ri-user-line"></i>
              Basic Information
            </div>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Full Name</span>
                <p className="info-value">{user.name}</p>
              </div>
              <div className="info-item">
                <span className="info-label">Email Address</span>
                <p className="info-value">{user.email}</p>
              </div>
              <div className="info-item">
                <span className="info-label">Phone Number</span>
                <p className="info-value">{user.phone || "Not provided"}</p>
              </div>
              <div className="info-item">
                <span className="info-label">User ID</span>
                <p className="info-value">#{user.id}</p>
              </div>
              <div className="info-item">
                <span className="info-label">Country</span>
                <p className="info-value">{user.country || "Not provided"}</p>
              </div>
              <div className="info-item">
                <span className="info-label">City</span>
                <p className="info-value">{user.city || "Not provided"}</p>
              </div>
            </div>
          </div>

          {/* Account Status */}
          <div className="info-card">
            <div className="section-title">
              <i className="ri-shield-check-line"></i>
              Account Status
            </div>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">User Type</span>
                <div>
                  <span className={`status-badge ${typeBadge.color}`}>
                    <i className="ri-user-settings-line"></i>
                    {typeBadge.text}
                  </span>
                </div>
              </div>
              <div className="info-item">
                <span className="info-label">Account Status</span>
                <div>
                  <span className={`status-badge ${statusBadge.color}`}>
                    <i className="ri-checkbox-circle-line"></i>
                    {statusBadge.text}
                  </span>
                </div>
              </div>
              <div className="info-item">
                <span className="info-label">Approval Status</span>
                <div>
                  <span className={`status-badge ${stateBadge.color}`}>
                    <i className="ri-shield-user-line"></i>
                    {stateBadge.text}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className="info-card">
            <div className="section-title">
              <i className="ri-verified-badge-line"></i>
              Verification Status
            </div>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Email Verification</span>
                <div>
                  <span
                    className={`status-badge ${
                      user.email_verified_at ? "success" : "warning"
                    }`}
                  >
                    <i
                      className={`${
                        user.email_verified_at
                          ? "ri-check-line"
                          : "ri-close-line"
                      }`}
                    ></i>
                    {user.email_verified_at ? "Verified" : "Not Verified"}
                  </span>
                </div>
              </div>
              <div className="info-item">
                <span className="info-label">Phone Verification</span>
                <div>
                  <span
                    className={`status-badge ${
                      user.phone_verified_at ? "success" : "warning"
                    }`}
                  >
                    <i
                      className={`${
                        user.phone_verified_at
                          ? "ri-check-line"
                          : "ri-close-line"
                      }`}
                    ></i>
                    {user.phone_verified_at ? "Verified" : "Not Verified"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Account Timeline */}
          <div className="info-card">
            <div className="section-title">
              <i className="ri-time-line"></i>
              Account Timeline
            </div>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Created At</span>
                <p className="info-value">{formatDate(user.created_at)}</p>
              </div>
              <div className="info-item">
                <span className="info-label">Last Updated</span>
                <p className="info-value">{formatDate(user.updated_at)}</p>
              </div>
            </div>
          </div>

          {/* Influencer Profile Information */}
          {user.profile && user.user_type === "Influencer" && (
            <div className="info-card">
              <div className="section-title">
                <i className="ri-profile-line"></i>
                Influencer Profile Information
              </div>

              {/* Profile Images */}
              {(user.profile.profile_image || user.profile.cover_image) && (
                <div className="mb-4">
                  <div
                    className="section-title"
                    style={{ fontSize: "13px", marginBottom: "16px" }}
                  >
                    <i className="ri-image-line"></i>
                    Profile Images
                  </div>
                  <div className="d-flex gap-3 flex-wrap">
                    {user.profile.profile_image && (
                      <div>
                        <span className="info-label mb-2 d-block">
                          Profile Image
                        </span>
                        <img
                          src={
                            user.profile.profile_image.startsWith("http")
                              ? user.profile.profile_image
                              : `https://api.tafaseelapp.com/storage/${user.profile.profile_image}`
                          }
                          alt="Profile"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                          }}
                        />
                      </div>
                    )}
                    {user.profile.cover_image && (
                      <div>
                        <span className="info-label mb-2 d-block">
                          Cover Image
                        </span>
                        <img
                          src={
                            user.profile.cover_image.startsWith("http")
                              ? user.profile.cover_image
                              : `https://api.tafaseelapp.com/storage/${user.profile.cover_image}`
                          }
                          alt="Cover"
                          style={{
                            width: "150px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Influencer Stats */}
              <div className="mb-4">
                <div
                  className="section-title"
                  style={{ fontSize: "13px", marginBottom: "16px" }}
                >
                  <i className="ri-bar-chart-line"></i>
                  Statistics
                </div>
                <div className="info-grid">
                  {user.profile.followers_count !== null &&
                    user.profile.followers_count !== undefined && (
                      <div className="info-item">
                        <span className="info-label">Followers Count</span>
                        <p className="info-value">
                          {typeof user.profile.followers_count === "number"
                            ? user.profile.followers_count.toLocaleString()
                            : user.profile.followers_count}
                        </p>
                      </div>
                    )}
                  {user.profile.age && (
                    <div className="info-item">
                      <span className="info-label">Age</span>
                      <p className="info-value">{user.profile.age}</p>
                    </div>
                  )}
                </div>
              </div>

              {user.profile.social_media && (
                <div className="mb-4">
                  <div
                    className="section-title"
                    style={{ fontSize: "13px", marginBottom: "16px" }}
                  >
                    <i className="ri-share-line"></i>
                    Social Media Profiles
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {user.profile.social_media.instagram && (
                      <div className="social-media-card instagram-card">
                        <div className="d-flex align-items-center">
                          <div className="social-media-icon-large instagram-icon me-3">
                            <i className="ri-instagram-line"></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-semibold">Instagram</h6>
                            <a
                              href={user.profile.social_media.instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none social-media-link-modal"
                              title="Open Instagram Profile"
                            >
                              <span className="text-break">
                                {user.profile.social_media.instagram}
                              </span>
                              <i className="ri-external-link-line ms-2"></i>
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                    {user.profile.social_media.facebook && (
                      <div className="social-media-card facebook-card">
                        <div className="d-flex align-items-center">
                          <div className="social-media-icon-large facebook-icon me-3">
                            <i className="ri-facebook-circle-line"></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-semibold">Facebook</h6>
                            <a
                              href={user.profile.social_media.facebook}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none social-media-link-modal"
                              title="Open Facebook Profile"
                            >
                              <span className="text-break">
                                {user.profile.social_media.facebook}
                              </span>
                              <i className="ri-external-link-line ms-2"></i>
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                    {!user.profile.social_media?.instagram &&
                      !user.profile.social_media?.facebook && (
                        <div className="empty-state">
                          <i className="ri-share-line"></i>
                          <p className="mb-0 fst-italic">
                            No social media profiles available
                          </p>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {user.profile.bio && (
                <div className="mb-4">
                  <div
                    className="section-title"
                    style={{ fontSize: "13px", marginBottom: "16px" }}
                  >
                    <i className="ri-file-text-line"></i>
                    Bio
                  </div>
                  <div className="info-item">
                    <p
                      className="info-value"
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        lineHeight: "1.6",
                      }}
                    >
                      {user.profile.bio}
                    </p>
                  </div>
                </div>
              )}

              {user.profile.categories &&
                user.profile.categories.length > 0 && (
                  <div>
                    <div
                      className="section-title"
                      style={{ fontSize: "13px", marginBottom: "16px" }}
                    >
                      <i className="ri-price-tag-3-line"></i>
                      Categories
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {user.profile.categories.map((category, index) => (
                        <span key={index} className="status-badge info">
                          <i className="ri-price-tag-3-line"></i>
                          {typeof category === "object" && category.name
                            ? category.name
                            : category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Business Profile Information */}
          {user.profile && user.user_type === "Business" && (
            <div className="info-card">
              <div className="section-title">
                <i className="ri-building-line"></i>
                Business Profile Information
              </div>

              {/* Profile Images */}
              {(user.profile.profile_image || user.profile.cover_image) && (
                <div className="mb-4">
                  <div
                    className="section-title"
                    style={{ fontSize: "13px", marginBottom: "16px" }}
                  >
                    <i className="ri-image-line"></i>
                    Profile Images
                  </div>
                  <div className="d-flex gap-3 flex-wrap">
                    {user.profile.profile_image && (
                      <div>
                        <span className="info-label mb-2 d-block">
                          Profile Image
                        </span>
                        <img
                          src={
                            user.profile.profile_image.startsWith("http")
                              ? user.profile.profile_image
                              : `https://api.tafaseelapp.com/storage/${user.profile.profile_image}`
                          }
                          alt="Profile"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                          }}
                        />
                      </div>
                    )}
                    {user.profile.cover_image && (
                      <div>
                        <span className="info-label mb-2 d-block">
                          Cover Image
                        </span>
                        <img
                          src={
                            user.profile.cover_image.startsWith("http")
                              ? user.profile.cover_image
                              : `https://api.tafaseelapp.com/storage/${user.profile.cover_image}`
                          }
                          alt="Cover"
                          style={{
                            width: "150px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Business Details */}
              <div className="mb-4">
                <div
                  className="section-title"
                  style={{ fontSize: "13px", marginBottom: "16px" }}
                >
                  <i className="ri-building-2-line"></i>
                  Business Details
                </div>
                <div className="info-grid">
                  {user.profile.brand_name && (
                    <div className="info-item">
                      <span className="info-label">Brand Name</span>
                      <p className="info-value">{user.profile.brand_name}</p>
                    </div>
                  )}
                  {user.profile.organization_name && (
                    <div className="info-item">
                      <span className="info-label">Organization Name</span>
                      <p className="info-value">
                        {user.profile.organization_name}
                      </p>
                    </div>
                  )}
                  {user.profile.has_multiple_brands !== null &&
                    user.profile.has_multiple_brands !== undefined && (
                      <div className="info-item">
                        <span className="info-label">Multiple Brands</span>
                        <div>
                          <span
                            className={`status-badge ${
                              user.profile.has_multiple_brands
                                ? "success"
                                : "secondary"
                            }`}
                          >
                            <i
                              className={`${
                                user.profile.has_multiple_brands
                                  ? "ri-check-line"
                                  : "ri-close-line"
                              }`}
                            ></i>
                            {user.profile.has_multiple_brands ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    )}
                  {user.profile.business_type && (
                    <div className="info-item">
                      <span className="info-label">Business Type</span>
                      <p className="info-value">{user.profile.business_type}</p>
                    </div>
                  )}
                  {user.profile.industry && (
                    <div className="info-item">
                      <span className="info-label">Industry</span>
                      <p className="info-value">{user.profile.industry}</p>
                    </div>
                  )}
                  {user.profile.website && (
                    <div className="info-item">
                      <span className="info-label">Website</span>
                      <p className="info-value">
                        <a
                          href={user.profile.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none social-media-link-modal"
                        >
                          {user.profile.website}
                          <i className="ri-external-link-line ms-2"></i>
                        </a>
                      </p>
                    </div>
                  )}
                  {user.profile.tax_id && (
                    <div className="info-item">
                      <span className="info-label">Tax ID</span>
                      <p className="info-value">{user.profile.tax_id}</p>
                    </div>
                  )}
                  {user.profile.registration_number && (
                    <div className="info-item">
                      <span className="info-label">Registration Number</span>
                      <p className="info-value">
                        {user.profile.registration_number}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {user.profile.description && (
                <div className="mb-4">
                  <div
                    className="section-title"
                    style={{ fontSize: "13px", marginBottom: "16px" }}
                  >
                    <i className="ri-file-text-line"></i>
                    Description
                  </div>
                  <div className="info-item">
                    <p
                      className="info-value"
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        lineHeight: "1.6",
                      }}
                    >
                      {user.profile.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <div className="d-flex gap-3 flex-wrap">
            {/* Approve User Button - Only show for non-approved users */}
            {user.state === 0 && user.user_type !== "Admin" && (
              <button
                className="modern-btn success"
                onClick={handleApproveUser}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <i className="ri-loader-2-line spin"></i>
                    Approving...
                  </>
                ) : (
                  <>
                    <i className="ri-check-double-line"></i>
                    Approve User
                  </>
                )}
              </button>
            )}

            {/* Activate/Deactivate Buttons */}
            {user.status !== "active" && user.user_type !== "Admin" && (
              <button
                className="modern-btn success"
                onClick={() => handleStatusUpdate("active")}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <i className="ri-loader-2-line spin"></i>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    Activate
                  </>
                )}
              </button>
            )}

            {user.status !== "inactive" && user.user_type !== "Admin" && (
              <button
                className="modern-btn warning"
                onClick={() => handleStatusUpdate("inactive")}
                disabled={isUpdating}
              >
                <i className="ri-close-line"></i>
                Deactivate
              </button>
            )}

            <button className="modern-btn secondary" onClick={toggle}>
              <i className="ri-close-line"></i>
              Close
            </button>
          </div>
        </ModalFooter>
      </Modal>
  );
}
